import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  ExternalLink
} from 'lucide-react';
import { rd, type RemoteData } from '@passionware/monads';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MissingBookingConfirmationTemplateError } from 'v5/api/booking/booking.api';
import { Alert, AlertDescription } from 'v5/design-system/Alert';
import { Button } from 'v5/design-system/Button';
import { Skeleton } from 'v5/design-system/Skeleton';
import { UserAvatar } from 'v5/patterns/widgets/user-avatar/UserAvatar';
import { cn } from 'v5/platform/dom/cn';
import type {
  ConfirmationRecipient,
  ConfirmationRecipientList,
  ConfirmationRecipientProblem
} from './useProductionConfirmationRecipients';

export type SendBookingConfirmationBulkBodyProps = {
  list: ConfirmationRecipientList;
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  previewRd: RemoteData<string>;
  templatesPath: string | null;
};

export function SendBookingConfirmationBulkBody({
  list,
  selectedIndex,
  onSelectIndex,
  previewRd,
  templatesPath
}: SendBookingConfirmationBulkBodyProps) {
  const { t } = useTranslation('productions', {
    keyPrefix: 'sendBookingConfirmationDialog'
  });
  const { ready, problems } = list;

  if (ready.length === 0 && problems.length === 0) {
    return (
      <p className="rounded-md border border-border-default bg-bg-subtle px-3 py-6 text-center text-sm text-fg-muted">
        {t('recipientsBulkEmpty')}
      </p>
    );
  }

  const safeIndex =
    ready.length === 0 ? 0 : Math.min(selectedIndex, ready.length - 1);
  const selected = ready[safeIndex];
  const showRecipientNav = ready.length > 1;
  const isSingleRecipientView = ready.length === 1 && problems.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {problems.length > 0 ? (
        <Alert variant="destructive" layout="horizontal">
          <CircleAlert />
          <AlertDescription>
            {t('recipientsMissingEmailMessage', {
              count: problems.length
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {ready.length > 0 || problems.length > 0 ? (
        <div className="flex shrink-0 flex-col gap-2">
          {ready.length > 0 && !isSingleRecipientView ? (
            <p className="text-sm font-medium text-fg-default">
              {t('recipientsBulkCount', { count: ready.length })}
            </p>
          ) : null}
          <ul
            className={
              problems.length + ready.length > 6
                ? 'max-h-56 overflow-y-auto'
                : undefined
            }
          >
            {problems.map(problem => (
              <ProblemRecipientRow key={problem.bookingId} problem={problem} />
            ))}
            {ready.map((recipient, index) => (
              <ReadyRecipientRow
                key={recipient.bookingId}
                recipient={recipient}
                selected={showRecipientNav && index === safeIndex}
                interactive={showRecipientNav}
                onSelect={() => onSelectIndex(index)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {ready.length === 0 && problems.length > 0 ? (
        <p className="rounded-md border border-border-default bg-bg-subtle px-3 py-6 text-center text-sm text-fg-muted">
          {t('recipientsBulkNoneReady')}
        </p>
      ) : null}

      {selected != null ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg-default">
                {t('templatePreviewLabel')}
              </p>
              {showRecipientNav ? (
                <p className="truncate text-xs text-fg-muted">
                  {t('recipientValue', {
                    name:
                      selected.resource.full_name.trim() ||
                      t('unknownRecipient'),
                    email: selected.emails.join(', ')
                  })}
                </p>
              ) : null}
            </div>
            {showRecipientNav ? (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  layout="icon"
                  disabled={safeIndex <= 0}
                  aria-label={t('previewPrevious')}
                  onClick={() => onSelectIndex(safeIndex - 1)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="min-w-12 text-center text-xs text-fg-muted">
                  {t('previewPosition', {
                    current: safeIndex + 1,
                    total: ready.length
                  })}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  layout="icon"
                  disabled={safeIndex >= ready.length - 1}
                  aria-label={t('previewNext')}
                  onClick={() => onSelectIndex(safeIndex + 1)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            ) : null}
          </div>
          {rd
            .journey(previewRd)
            .wait(<Skeleton className="h-48 w-full" />)
            .catch(error =>
              error instanceof MissingBookingConfirmationTemplateError ? (
                <EmptyTemplateState
                  templatesPath={templatesPath}
                  emptyLabel={t('templateEmpty')}
                  goToTemplatesLabel={t('goToTemplates')}
                />
              ) : (
                <p className="text-sm text-fg-danger">
                  {t('templateLoadFailed')}
                </p>
              )
            )
            .map(html =>
              html.trim() ? (
                <div className="relative min-h-0 flex-1">
                  <ConfirmationPreviewIframe
                    html={html}
                    title={t('templatePreviewLabel')}
                  />
                  {rd.isPlaceholderData(previewRd) ? (
                    <Skeleton className="absolute inset-0 rounded-md" />
                  ) : null}
                </div>
              ) : (
                <EmptyTemplateState
                  templatesPath={templatesPath}
                  emptyLabel={t('templateEmpty')}
                  goToTemplatesLabel={t('goToTemplates')}
                />
              )
            )}
        </div>
      ) : null}
    </div>
  );
}

const PREVIEW_SRC_PREFIX =
  '<!DOCTYPE html><html><head><meta charset="utf-8" /><base target="_blank" /><style>html,body{margin:0;}body{box-sizing:border-box;padding:16px;font-family:system-ui,sans-serif;font-size:14px;line-height:1.45;color:#111;}img{max-width:100%;height:auto;}</style></head><body>';
const PREVIEW_SRC_SUFFIX = '</body></html>';

function ConfirmationPreviewIframe({
  html,
  title
}: {
  html: string;
  title: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc == null) {
      return;
    }
    const next = Math.ceil(
      Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0)
    );
    if (next > 0) {
      setHeight(next);
    }
  }, []);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border-default bg-bg-default">
      <iframe
        ref={iframeRef}
        title={title}
        sandbox="allow-same-origin"
        srcDoc={`${PREVIEW_SRC_PREFIX}${html}${PREVIEW_SRC_SUFFIX}`}
        onLoad={measure}
        className={cn('block w-full border-0', height == null && 'h-48')}
        style={height == null ? undefined : { height }}
      />
    </div>
  );
}

function ReadyRecipientRow({
  recipient,
  selected,
  interactive,
  onSelect
}: {
  recipient: ConfirmationRecipient;
  selected: boolean;
  interactive: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('productions', {
    keyPrefix: 'sendBookingConfirmationDialog'
  });
  const content = (
    <>
      <UserAvatar
        user={rd.of({
          avatarUrl: recipient.resource.profile_picture,
          name: recipient.resource.full_name
        })}
        size="sm"
      />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {recipient.resource.full_name.trim() || t('unknownRecipient')}
      </span>
      <span
        className={cn(
          'max-w-48 truncate text-sm',
          selected ? 'text-fg-accent' : 'text-fg-muted'
        )}
      >
        {recipient.emails[0]}
      </span>
    </>
  );

  return (
    <li>
      {interactive ? (
        <button
          type="button"
          onClick={onSelect}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors',
            selected ? 'bg-bg-accent text-fg-accent' : 'hover:bg-bg-subtle'
          )}
          aria-current={selected ? 'true' : undefined}
        >
          {content}
        </button>
      ) : (
        <div className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5">
          {content}
        </div>
      )}
    </li>
  );
}

function ProblemRecipientRow({
  problem
}: {
  problem: ConfirmationRecipientProblem;
}) {
  const { t } = useTranslation('productions', {
    keyPrefix: 'sendBookingConfirmationDialog'
  });
  return (
    <li className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
      <UserAvatar
        user={rd.of({
          avatarUrl: problem.resource.profile_picture,
          name: problem.resource.full_name
        })}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg-default">
          {problem.resource.full_name.trim() || t('unknownRecipient')}
        </p>
        <p className="truncate text-sm text-fg-danger">
          {t('recipientProblemMissingEmail')}
        </p>
      </div>
    </li>
  );
}

function EmptyTemplateState({
  templatesPath,
  emptyLabel,
  goToTemplatesLabel
}: {
  templatesPath: string | null;
  emptyLabel: string;
  goToTemplatesLabel: string;
}) {
  return (
    <div className="space-y-2 rounded-md border border-border-default bg-bg-subtle px-3 py-10 text-center text-sm text-fg-muted">
      <p>{emptyLabel}</p>
      {templatesPath ? (
        <Link
          to={templatesPath}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-fg-accent hover:underline"
        >
          {goToTemplatesLabel}
          <ExternalLink className="size-3.5 shrink-0" aria-hidden />
        </Link>
      ) : null}
    </div>
  );
}
